import planningRecordService from '../../services/planningRecordService'
import type { FieldValues, ResolverResult } from 'react-hook-form'
import validators from '../validators'
import {
  allPlanningClassifications,
  PlanningClassification,
  allPlanningCategories,
  PlanningCategory,
  allPlanningApplicationTypes,
  PlanningApplicationType,
  PostcodeDistrict,
} from '@leadgen/models/client'

export type FormData = {
  id: string
  recipientType: 'applicant' | 'agent'
  applicantTitle: string | null
  applicantFirstName: string | null
  applicantLastName: string | null
  applicantCompanyName: string | null
  applicantAddress1: string | null
  applicantAddress2: string | null
  applicantTown: string | null
  applicantPostcode: string | null

  agentTitle: string | null
  agentFirstName: string | null
  agentLastName: string | null
  agentCompanyName: string | null
  agentAddress1: string | null
  agentAddress2: string | null
  agentTown: string | null
  agentPostcode: string | null

  initialAddress: string | null
  initialPostcode: string | null
  initialPostcodeDistrict: PostcodeDistrict | null
  address1: string | null
  address2: string | null
  town: string | null
  postcode: string | null
  postcodeDistrict: PostcodeDistrict | null

  categories: PlanningCategory[]
  classifications: PlanningClassification[]
  applicationType: PlanningApplicationType | null
}
export type Resolver<TFieldValues extends FieldValues = FieldValues> = (
  values: TFieldValues,
) => Promise<ResolverResult<TFieldValues>> | ResolverResult<TFieldValues>
class PlanningRecordForm {
  resolver: Resolver<FormData> = async values => {
    const normalizedValues: FormData = {
      id: values.id ?? '',
      recipientType: values.recipientType ?? 'applicant',
      applicantTitle: values.applicantTitle ?? null,
      applicantFirstName: values.applicantFirstName ?? null,
      applicantLastName: values.applicantLastName ?? null,
      applicantCompanyName: values.applicantCompanyName ?? null,
      applicantAddress1: values.applicantAddress1 ?? null,
      applicantAddress2: values.applicantAddress2 ?? null,
      applicantTown: values.applicantTown ?? null,
      applicantPostcode: values.applicantPostcode ?? null,
      agentTitle: values.agentTitle ?? null,
      agentFirstName: values.agentFirstName ?? null,
      agentLastName: values.agentLastName ?? null,
      agentCompanyName: values.agentCompanyName ?? null,
      agentAddress1: values.agentAddress1 ?? null,
      agentAddress2: values.agentAddress2 ?? null,
      agentTown: values.agentTown ?? null,
      agentPostcode: values.agentPostcode ?? null,
      initialAddress: values.initialAddress ?? null,
      initialPostcode: values.initialPostcode ?? null,
      initialPostcodeDistrict: values.initialPostcodeDistrict ?? null,
      address1: values.address1 ?? null,
      address2: values.address2 ?? null,
      town: values.town ?? null,
      postcode: values.postcode ?? null,
      postcodeDistrict: values.postcodeDistrict ?? null,
      categories: values.categories ?? [],
      classifications: values.classifications ?? [],
      applicationType: values.applicationType ?? null,
    }
    const errors: [keyof FormData, { type: 'custom' | 'format'; message: string }][] = []
    const { applicationType, categories, classifications } = normalizedValues
    const applicationTypeError = await this.validateApplicationTypeField(
      applicationType,
      normalizedValues,
    )
    if (applicationTypeError) {
      errors.push([
        'applicationType',
        {
          type: 'custom',
          message: applicationTypeError,
        },
      ])
    }
    const categoriesError = await this.validatePlanningCategoriesField(categories, normalizedValues)
    if (categoriesError) {
      errors.push([
        'categories',
        {
          type: 'custom',
          message: categoriesError,
        },
      ])
    }
    const classificationsError = await this.validateClassificationsField(
      classifications,
      normalizedValues,
    )
    if (classificationsError) {
      errors.push([
        'classifications',
        {
          type: 'custom',
          message: classificationsError,
        },
      ])
    }

    const titleField: 'applicantTitle' | 'agentTitle' =
      normalizedValues.recipientType === 'applicant' ? 'applicantTitle' : 'agentTitle'
    const firstNameField: 'applicantFirstName' | 'agentFirstName' =
      normalizedValues.recipientType === 'applicant' ? 'applicantFirstName' : 'agentFirstName'
    const lastNameField: 'applicantLastName' | 'agentLastName' =
      normalizedValues.recipientType === 'applicant' ? 'applicantLastName' : 'agentLastName'
    const companyNameField: 'applicantCompanyName' | 'agentCompanyName' =
      normalizedValues.recipientType === 'applicant' ? 'applicantCompanyName' : 'agentCompanyName'
    const recipientPostcodeField: 'applicantPostcode' | 'agentPostcode' =
      normalizedValues.recipientType === 'applicant' ? 'applicantPostcode' : 'agentPostcode'
    const recipientAddress1Field: 'applicantAddress1' | 'agentAddress1' =
      normalizedValues.recipientType === 'applicant' ? 'applicantAddress1' : 'agentAddress1'
    const hasTitleAndLast = normalizedValues[titleField] && normalizedValues[lastNameField]
    const hasFirstAndLast = normalizedValues[firstNameField] && normalizedValues[lastNameField]
    const hasCompany = normalizedValues[companyNameField]
    if (!(hasTitleAndLast || hasFirstAndLast || hasCompany)) {
      errors.push([
        titleField,
        {
          message: 'Please provide title + last name, or first name + last name, or company name',
          type: 'custom',
        },
      ])
      errors.push([
        firstNameField,
        {
          message: 'Please provide title + last name, or first name + last name, or company name',
          type: 'custom',
        },
      ])
      errors.push([
        lastNameField,
        {
          message: 'Please provide title + last name, or first name + last name, or company name',
          type: 'custom',
        },
      ])
      errors.push([
        companyNameField,
        {
          message: 'Please provide title + last name, or first name + last name, or company name',
          type: 'custom',
        },
      ])
    } else {
      const titleError = await this.validateTitleField(
        normalizedValues[titleField],
        normalizedValues,
      )
      if (titleError) {
        errors.push([
          titleField,
          {
            type: 'format',
            message: titleError,
          },
        ])
      }
      const firstNameError = await this.validateFirstNameField(
        normalizedValues[firstNameField],
        normalizedValues,
      )
      if (firstNameError) {
        errors.push([
          firstNameField,
          {
            type: 'format',
            message: firstNameError,
          },
        ])
      }
      const lastNameError = await this.validateLastNameField(
        normalizedValues[lastNameField],
        normalizedValues,
      )
      if (lastNameError) {
        errors.push([
          lastNameField,
          {
            type: 'format',
            message: lastNameError,
          },
        ])
      }
      const companyNameError = await this.validateCompanyNameField(
        normalizedValues[companyNameField],
        normalizedValues,
      )
      if (companyNameError) {
        errors.push([
          companyNameField,
          {
            type: 'format',
            message: companyNameError,
          },
        ])
      }
    }
    const recipientPostcodeError = await this.validatePostcodeField(
      normalizedValues[recipientPostcodeField],
      normalizedValues,
    )
    if (recipientPostcodeError) {
      errors.push([
        recipientPostcodeField,
        {
          type: recipientPostcodeError === 'Please enter a post code' ? 'custom' : 'format',
          message: recipientPostcodeError,
        },
      ])
    }
    const recipientAddress1Error = await this.validateAddress1Field(
      normalizedValues[recipientAddress1Field],
      normalizedValues,
    )
    if (recipientAddress1Error) {
      errors.push([
        recipientAddress1Field,
        {
          type: 'format',
          message: recipientAddress1Error,
        },
      ])
    }
    const postcodeError = await this.validatePostcodeField(
      normalizedValues['postcode'],
      normalizedValues,
    )
    const postcodeDistrictError = await this.validatePostcodeDistrictField(
      normalizedValues['postcodeDistrict'],
      normalizedValues,
    )
    if (postcodeError || postcodeDistrictError) {
      errors.push([
        'postcode',
        {
          type: postcodeError === 'Please enter a post code' ? 'custom' : 'format',
          message: (postcodeError || postcodeDistrictError) as string,
        },
      ])
    }
    const hasInitialAddress = normalizedValues['initialAddress']
    const hasInitialPostcode = normalizedValues['initialPostcode']
    const hasInitialPostcodeDistrict = normalizedValues['initialPostcodeDistrict']

    const address1Error = await this.validateAddress1Field(
      normalizedValues['address1'],
      normalizedValues,
    )
    if (
      address1Error &&
      (!hasInitialAddress || !hasInitialPostcode || !hasInitialPostcodeDistrict)
    ) {
      errors.push([
        'address1',
        {
          type: 'format',
          message: address1Error,
        },
      ])
    }
    return {
      values: errors.length ? {} : normalizedValues,
      errors: errors.reduce(
        (memo, next) => {
          memo[next[0]] = next[1]
          return memo
        },
        <Record<keyof FormData, { type: 'custom' | 'format'; message: string }>>{},
      ),
    }
  }
  onSubmitApprove = async (values: FormData): Promise<void> => {
    const {
      id,
      recipientType,
      applicantCompanyName,
      applicantTitle,
      applicantFirstName,
      applicantLastName,
      applicantAddress1,
      applicantAddress2,
      applicantPostcode,
      applicantTown,
      agentCompanyName,
      agentTitle,
      agentFirstName,
      agentLastName,
      agentAddress1,
      agentAddress2,
      agentPostcode,
      agentTown,
      classifications,
      initialAddress,
      initialPostcode,
      initialPostcodeDistrict,
      address1,
      address2,
      town,
      postcode,
      postcodeDistrict,
      categories,
      applicationType,
    } = values
    const { errors } = await this.resolver(values)
    if (Object.keys(errors).length) {
      throw new Error('Failed to submit application. Please try again')
    }
    const address =
      !initialAddress || !initialPostcode || !initialPostcodeDistrict
        ? [address1, address2, town, postcode].filter(Boolean).join(', ')
        : initialAddress
    return planningRecordService.approvePlanningRecord({
      planningRecord: {
        id,
        recipientType,
        applicantCompanyName,
        applicantTitle,
        applicantFirstName,
        applicantLastName,
        applicantAddress1,
        applicantAddress2,
        applicantPostcode,
        applicantTown,
        agentCompanyName,
        agentTitle,
        agentFirstName,
        agentLastName,
        agentAddress1,
        agentAddress2,
        agentPostcode,
        agentTown,
        categories,
        classifications,
        address,
        postcode,
        postcodeDistrict,
        applicationType,
      },
    })
  }
  onSubmitDiscard = async (values: { planningRecordId: string }): Promise<void> => {
    const { planningRecordId } = values
    return planningRecordService.discardPlanningRecord({
      planningRecordId,
    })
  }
  validateCompanyNameField = validators.combineValidators(
    validators.validateLengthMax({
      max: 256,
      errorMessage: 'This value is too long.',
    }),
  )
  validateTitleField = validators.combineValidators(
    validators.validateLengthMax({
      max: 50,
      errorMessage: 'This value is too long.',
    }),
  )
  validateFirstNameField = validators.combineValidators(
    validators.validateLengthMax({
      max: 256,
      errorMessage: 'This value is too long.',
    }),
  )
  validateLastNameField = validators.combineValidators(
    validators.validateLengthMax({
      max: 256,
      errorMessage: 'This value is too long.',
    }),
  )
  validateAddress1Field = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please enter an address',
    }),
    validators.validateLengthMax({
      max: 256,
      errorMessage: 'This value is too long.',
    }),
  )
  validateAddress2Field = validators.combineValidators(
    validators.validateLengthMax({
      max: 256,
      errorMessage: 'This value is too long.',
    }),
  )
  validateTownField = validators.combineValidators(
    validators.validateLengthMax({
      max: 256,
      errorMessage: 'This value is too long.',
    }),
  )
  validateAddressField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please enter an address',
    }),
    validators.validateLengthMax({
      max: 256,
      errorMessage: 'This value is too long.',
    }),
  )
  validatePostcodeField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please enter a post code',
    }),
    validators.validatePostcode({
      errorMessage: 'Please enter a valid post code',
    }),
    validators.validateLengthMax({
      max: 256,
      errorMessage: 'This value is too long.',
    }),
  )
  validatePostcodeDistrictField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please enter a valid post code',
    }),
  )
  validatePlanningCategoriesField = validators.combineValidators((value: PlanningCategory[]) => {
    if (!value.length) {
      return 'Please select one or more planning categories e.g. Conversions'
    }
    if (!value.every(option => allPlanningCategories.includes(option))) {
      return 'Invalid planning categories'
    }
  })
  validateClassificationsField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please select one or more planning classifications',
    }),
    validators.validateOptionsMulti<PlanningClassification>({
      errorMessage: 'Values must be planning classifications',
      options: allPlanningClassifications,
    }),
  )
  validateApplicationTypeField = validators.combineValidators(
    validators.validateRequired({
      errorMessage: 'Please select an application type',
    }),
    validators.validateOneOf<PlanningApplicationType>({
      errorMessage: 'Value must be a planning application type',
      options: allPlanningApplicationTypes,
    }),
  )
}

export default new PlanningRecordForm()
